import { Badge, Button, FormField, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReceiptDetail } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiRequestError } from '../../../lib/api-error';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { receivingApi, receivingKeys } from '../api';
import { ScanField } from '../components/ScanField';

/**
 * CO2 cage session (spec §5.17).
 *
 * Every tube has a unique serial and there can be hundreds per cage, so the
 * loop has to be: scan, hear the counter tick, scan again. Each scan is its own
 * command — a duplicate is rejected AT the scan (while the tube is still in the
 * operator's hand) instead of at the end of the cage, and nothing is lost if
 * the tablet dies mid-cage because the tubes are already recorded.
 */
export function CagePanel({ receipt }: { receipt: ReceiptDetail }): ReactNode {
  const { t } = useTranslation('receiving');
  const queryClient = useQueryClient();

  const [cageCode, setCageCode] = useState('');
  const [expectedCount, setExpectedCount] = useState('');
  const [scanCount, setScanCount] = useState(0);

  const closed = receipt.status === 'closed';

  const summary = useQuery({
    queryKey: receivingKeys.cage(cageCode),
    queryFn: () => receivingApi.cage(cageCode),
    enabled: cageCode.trim().length > 1,
  });

  const scanTube = useMutation({
    mutationFn: (serial: string) =>
      receivingApi.cageScan({ cageCode: cageCode.trim(), serial, receiptId: receipt.id }),
    onSuccess: async (result) => {
      if (result.outcome === 'moved') {
        toastInfo(t('cage.moved', { serial: result.serial }));
      } else {
        toastSuccess(t('cage.added', { serial: result.serial }));
      }
      setScanCount((n) => n + 1);
      await queryClient.invalidateQueries({ queryKey: receivingKeys.cage(cageCode) });
    },
    onError: (error) => {
      // A duplicate is the expected failure here, and it deserves its own
      // sentence rather than a generic conflict toast.
      if (isApiRequestError(error) && error.params?.['reason'] === 'duplicate_serial') {
        toastError(new Error(t('cage.duplicate', { serial: String(error.params['serial'] ?? '') })));
        return;
      }
      toastError(error);
    },
  });

  const closeCage = useMutation({
    mutationFn: () =>
      receivingApi.cageClose({
        cageCode: cageCode.trim(),
        receiptId: receipt.id,
        expectedCount: expectedCount ? Number(expectedCount) : null,
      }),
    onSuccess: (result) => {
      if (result.countMatches) {
        toastSuccess(t('cage.closed', { count: result.count }));
      } else {
        toastInfo(t('cage.countMismatch', { count: result.count, expected: result.expectedCount }));
      }
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const count = summary.data?.count ?? 0;

  return (
    <Surface className="space-y-4 p-4">
      <div>
        <h3 className="text-sm font-medium text-ink">{t('cage.title')}</h3>
        <p className="mt-1 text-xs text-ink-muted">{t('cage.hint')}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label={t('cage.cageCode')}
          placeholder={t('cage.cagePlaceholder')}
          value={cageCode}
          onChange={(event) => {
            setCageCode(event.target.value);
          }}
          disabled={closed}
        />
        <FormField
          label={t('cage.expectedCount')}
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          value={expectedCount}
          onChange={(event) => {
            setExpectedCount(event.target.value);
          }}
          disabled={closed}
        />
      </div>

      <ScanField
        label={t('cage.serialPlaceholder')}
        placeholder={t('cage.serialPlaceholder')}
        disabled={closed || cageCode.trim().length < 2}
        busy={scanTube.isPending}
        refocusKey={scanCount}
        onScan={(serial) => {
          scanTube.mutate(serial);
        }}
        testId="cage-scan"
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">{t('cage.count')}</span>
          <span className="font-mono text-2xl tabular-nums text-ink" data-testid="cage-count">
            {count}
          </span>
        </div>
        <Button
          variant="secondary"
          disabled={closed || cageCode.trim().length < 2}
          loading={closeCage.isPending}
          onClick={() => {
            closeCage.mutate();
          }}
        >
          {t('cage.close')}
        </Button>
      </div>

      <div>
        <div className="mb-1 text-xs uppercase tracking-wide text-ink-muted">{t('cage.scanned')}</div>
        {count === 0 ? (
          <p className="text-sm text-ink-muted">{t('cage.empty')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {(summary.data?.serials ?? []).map((asset) => (
              <li key={asset.assetId}>
                <Badge tone="neutral" size="sm">
                  <span className="font-mono">{asset.serial}</span>
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Surface>
  );
}
